import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useLayout } from '../../../hooks/screen/useLayout';

import { UseQueryResult, useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getProducts } from '../../../api/product/queries/getProducts';
import { MainHead } from '../../../components/common/head/MainHead';
import { ProductStatusConstant } from '../../../constants/product/product.constant';
import { useGetHomeImagesQuery } from '../../../hooks/api/home/queries/useGetHomeImages.query';
import { useGetWeeklyProductViewsQuery } from '../../../hooks/api/home/queries/useGetWeeklyProductViews.query';
import { useGetInformationsQuery } from '../../../hooks/api/information/queries/useGetInformations.query';
import { useGetOpenMarketProductsQuery } from '../../../hooks/api/product/queries/useGetOpenMarketProducts.query';
import { useGetProductsQuery } from '../../../hooks/api/product/queries/useGetProducts.query';
import { IProduct } from '../../../interfaces/Product.interfaces';
import { mapOpenMarketProductToCommonListView } from '../../../utils/mapOpenMarketProductToCommonListView.util';
import { InfoHorizontalListDetailViewGroup } from '../../../views/Info/listView/InfoHorizontalListDetailView/InfoHorizontalListDetailViewGroup';
import { ProductHorizontalListDetailViewGroup } from '../../../views/Product/listView/ProductHorizontalListDetailView/ProductHorizontalListDetailViewGroup';
import { ProductHorizontalListSummaryViewGroupWrapper } from '../../../views/Product/listView/ProductHorizontalListSummaryView/ProductHorizontalListSummaryViewGroupWrapper';
import { DirectMenuSection } from './components/DirectMenuSection';
import { GoToJoinSection } from './components/GoToJoinSection';
import { GoToUserSellTractorSection } from './components/GoToUserSellTractorSection';
import { MainCarousel } from './components/MainCarousel';
import { RecommendedProductsSection } from './components/RecommendedProductsSection';
import { YoutubeReviewSection } from './components/YoutubeReviewSection';

export const HomePage = () => {
  const [recommendedProducts, setRecommendedProducts] = useState<IProduct[]>([]);

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: false }} hasPluvaLogo hasMyInfoButton />,
    fixedHeaderHeight: 40,
  });

  const { data: mainCarouselImages, isLoading: isGetMainCarouselImagesQueryLoading } = useGetHomeImagesQuery({
    searchParam: {
      category: 'CAROUSEL_MAIN',
    },
  });

  const { data: pluvaProducts, isLoading: isGetPluvaProductsQueryLoading } = useGetProductsQuery({
    searchParam: {
      category: 'PLUVA',
      status: ProductStatusConstant.ONSALE,
      count: 5,
    },
  });

  const { data: partnerProducts, isLoading: isGetPartnerProductsQueryLoading } = useGetProductsQuery({
    searchParam: {
      category: 'PARTNER',
      status: ProductStatusConstant.ONSALE,
      count: 5,
    },
  });

  const { data: openMarketProducts, isLoading: isGetOpenMarketProductsQueryLoading } = useGetOpenMarketProductsQuery({
    searchParam: {
      status: ProductStatusConstant.ONSALE,
      count: 5,
    },
  });

  const openMarketProductsToCommon = openMarketProducts?.map((openMarketProduct) => {
    return mapOpenMarketProductToCommonListView(openMarketProduct);
  });

  const { data: informations, isLoading: isGetInformationsQueryLoading } = useGetInformationsQuery({
    searchParam: { category: 'MAGAZINE', count: 5 },
  });

  const allProductsQueries = useQueries({
    queries: [
      {
        queryKey: ['all_partner_products_on_sale'],
        queryFn: () =>
          getProducts({
            searchParam: {
              category: 'PARTNER',
              status: ProductStatusConstant.ONSALE,
            },
          }),
      },
      {
        queryKey: ['all_pluva_products_on_sale'],
        queryFn: () =>
          getProducts({
            searchParam: {
              category: 'PLUVA',
              status: ProductStatusConstant.ONSALE,
            },
          }),
      },
      {
        queryKey: ['all_open_market_products_on_sale'],
        queryFn: () =>
          getProducts({
            searchParam: {
              category: 'OPEN_MARKET',
              status: ProductStatusConstant.ONSALE,
            },
          }),
      },
    ],
  });

  const { data: weeklyProductViews, isLoading: isGetWeeklyProductViewsQueryLoading } = useGetWeeklyProductViewsQuery();
  const formattedViews = Number(weeklyProductViews)?.toLocaleString('ko-KR');

  useEffect(() => {
    const combinedProducts = allProductsQueries.reduce((acc: IProduct[], curr: UseQueryResult<IProduct[]>) => {
      if (!curr.isError && !curr.isLoading && curr.data) {
        const filteredProducts: IProduct[] = curr.data.filter((product: IProduct) => {
          return !!product.recommendedYn;
        });
        return acc.concat(filteredProducts);
      }
      return acc;
    }, []);

    setRecommendedProducts(combinedProducts);
  }, [allProductsQueries[0].data, allProductsQueries[1].data, allProductsQueries[2].data]);

  if (
    isGetMainCarouselImagesQueryLoading ||
    isGetPluvaProductsQueryLoading ||
    isGetPartnerProductsQueryLoading ||
    isGetOpenMarketProductsQueryLoading ||
    isGetInformationsQueryLoading ||
    isGetWeeklyProductViewsQueryLoading
  )
    return <AnimateLoading />;

  return (
    <>
      <Helmet>
        <title>{pageInfo.homePage.title}</title>
      </Helmet>

      <Container>
        <TopSection>
          {mainCarouselImages && mainCarouselImages.length > 0 && (
            <MainCarousel carouselImages={mainCarouselImages} pageType={'home'} />
          )}
          <TopSectionText>
            {'지난주에 고객님들이 '}
            <TopSectionTextHighlight>
              {formattedViews}
              {'번'}
            </TopSectionTextHighlight>
            {' 매물을 살펴봤어요!'}
          </TopSectionText>
          <DirectMenuSection />
        </TopSection>

        {recommendedProducts && recommendedProducts.length > 0 && (
          <RecommendedProductsSection products={recommendedProducts} />
        )}

        <YoutubeReviewSection />

        {partnerProducts && partnerProducts.length >= 3 && (
          <ProductHorizontalListSummaryViewGroupWrapper
            products={partnerProducts}
            category={'partner'}
            pageType={'home'}
            title={'믿을 수 있는 협력사 매물'}
          />
        )}

        {pluvaProducts && (
          <ContentWrapper>
            <ContentTitle>{'직접 보증하는 플루바 매물'}</ContentTitle>
            <ProductHorizontalListDetailViewGroup pageType={'home'} category={'pluva'} products={pluvaProducts} />
          </ContentWrapper>
        )}
        <GoToUserSellTractorSection />

        {openMarketProductsToCommon && openMarketProductsToCommon.length >= 3 && (
          <ProductHorizontalListSummaryViewGroupWrapper
            products={openMarketProductsToCommon}
            category={'open-market'}
            pageType={'home'}
            title={'회원 직거래 매물'}
          />
        )}

        {informations && (
          <ContentWrapper>
            <ContentTitle>{'읽을수록 도움되는 꿀정보'}</ContentTitle>
            <InfoHorizontalListDetailViewGroup pageType={'home'} informations={informations.slice(0, 5)} />
          </ContentWrapper>
        )}

        <GoToJoinSection />
      </Container>
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  padding-bottom: 32px;

  align-self: stretch;
`;

const TopSectionText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const TopSectionTextHighlight = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  font-weight: 700;
`;

const ContentWrapper = styled.div`
  width: 100%;

  padding: 0px 16px 32px 16px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  gap: 16px;
`;

const ContentTitle = styled.h3`
  width: 100%;

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 17px */
`;

const EventModalImage = styled.img<{ bgColor?: string }>`
  width: 100%;

  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  background: ${(props) => (props.bgColor ? props.bgColor : '')};
`;

const EventBannerImage = styled.img`
  width: 100%;

  cursor: pointer;
`;
